import React, { useState } from 'react';
import {
	Datagrid,
	List,
	TextField,
	ReferenceInput,
	AutocompleteInput,
	Filter,
	TextInput,
	SelectInput,
	FunctionField,
	useListContext,
	useNotify,
	useUnselectAll,
	TopToolbar,
	Button as RaButton,
	ExportButton,
	CreateButton,
} from 'react-admin';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	Checkbox,
	TextField as MuiTextField,
	Stack,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import SyncIcon from '@mui/icons-material/Sync';
import {
	forceBatch,
	historyBatch,
	IForceBatchItem,
} from '../../Services/connectorAccountProcess';
import { syncAllSchemas } from '../../Services/quantiAI';

// #region useConnectorAccounts
const useConnectorAccounts = () => {
	const { selectedIds, data } = useListContext();

	const getConnectorAccounts = (): IForceBatchItem[] => {
		if (!selectedIds || selectedIds.length === 0) return [];

		return selectedIds
			.map((id: string) => {
				const record = data?.find((r: any) => r.id === id);
				if (!record) return null;
				return {
					projectFolderId: record.projectFolderId,
					sku: record.sku,
					accountId: record.accountId,
				};
			})
			.filter(Boolean) as IForceBatchItem[];
	};

	return { selectedIds, getConnectorAccounts };
};

// #region Constants
const DEFAULT_PROCESSOR_IMAGE = 'rg.fr-par.scw.cloud/core-services/processor-v2:latest-dev';

// #region ForceModal
interface ForceModalOptions {
	cleanupOldProcessData: boolean;
	testMode: boolean;
	connectorVersion: string;
	processorVersion: string;
}

interface ForceModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (options: ForceModalOptions) => void;
	loading: boolean;
	count: number;
}

const ForceModal = ({
	open,
	onClose,
	onConfirm,
	loading,
	count,
}: ForceModalProps) => {
	const [cleanupOldProcessData, setCleanupOldProcessData] = useState(false);
	const [testMode, setTestMode] = useState(false);
	const [connectorVersion, setConnectorVersion] = useState('');
	const [processorVersion, setProcessorVersion] = useState(DEFAULT_PROCESSOR_IMAGE);

	const handleConfirm = () => {
		onConfirm({
			cleanupOldProcessData,
			testMode,
			connectorVersion,
			processorVersion,
		});
	};

	const handleClose = () => {
		setCleanupOldProcessData(false);
		setTestMode(false);
		setConnectorVersion('');
		setProcessorVersion(DEFAULT_PROCESSOR_IMAGE);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Lancer Force ({count} connecteurs)</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={cleanupOldProcessData}
								onChange={(e) => setCleanupOldProcessData(e.target.checked)}
							/>
						}
						label="Nettoyer les anciennes données (cleanup)"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={testMode}
								onChange={(e) => setTestMode(e.target.checked)}
								color="warning"
							/>
						}
						label="Mode test E2E (écrit dans BQ de test, pas de facturation)"
					/>
					{testMode && (
						<>
							<MuiTextField
								label="Version connecteur (optionnel)"
								placeholder="ex: v0.0.0"
								value={connectorVersion}
								onChange={(e) => setConnectorVersion(e.target.value)}
								size="small"
								fullWidth
							/>
							<MuiTextField
								label="Image processor (optionnel)"
								placeholder="ex: rg.fr-par.scw.cloud/core-services/processor-v2:latest-dev"
								value={processorVersion}
								onChange={(e) => setProcessorVersion(e.target.value)}
								size="small"
								fullWidth
							/>
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Annuler
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color={testMode ? 'warning' : 'primary'}
					disabled={loading}
					startIcon={
						loading ? <CircularProgress size={16} /> : <PlayArrowIcon />
					}
				>
					{testMode ? 'Lancer (Test)' : 'Lancer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// #region HistoryModal
interface HistoryModalOptions {
	startDate: string;
	endDate: string;
	cleanupOldProcessData: boolean;
	testMode: boolean;
	connectorVersion: string;
	processorVersion: string;
}

interface HistoryModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (options: HistoryModalOptions) => void;
	loading: boolean;
	count: number;
}

const HistoryModal = ({
	open,
	onClose,
	onConfirm,
	loading,
	count,
}: HistoryModalProps) => {
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [cleanupOldProcessData, setCleanupOldProcessData] = useState(false);
	const [testMode, setTestMode] = useState(false);
	const [connectorVersion, setConnectorVersion] = useState('');
	const [processorVersion, setProcessorVersion] = useState(DEFAULT_PROCESSOR_IMAGE);

	const handleConfirm = () => {
		if (!startDate || !endDate) return;
		// Convertir en format RFC3339
		const start = new Date(startDate).toISOString();
		const end = new Date(endDate).toISOString();
		onConfirm({
			startDate: start,
			endDate: end,
			cleanupOldProcessData,
			testMode,
			connectorVersion,
			processorVersion,
		});
	};

	const handleClose = () => {
		setStartDate('');
		setEndDate('');
		setCleanupOldProcessData(false);
		setTestMode(false);
		setConnectorVersion('');
		setProcessorVersion(DEFAULT_PROCESSOR_IMAGE);
		onClose();
	};

	const isValid = startDate && endDate && new Date(startDate) <= new Date(endDate);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Reprise d'historique ({count} connecteurs)</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<MuiTextField
						label="Date de début"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
					/>
					<MuiTextField
						label="Date de fin"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={cleanupOldProcessData}
								onChange={(e) => setCleanupOldProcessData(e.target.checked)}
							/>
						}
						label="Nettoyer les anciennes données (cleanup)"
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={testMode}
								onChange={(e) => setTestMode(e.target.checked)}
								color="warning"
							/>
						}
						label="Mode test E2E (écrit dans BQ de test, pas de facturation)"
					/>
					{testMode && (
						<>
							<MuiTextField
								label="Version connecteur (optionnel)"
								placeholder="ex: v0.0.0"
								value={connectorVersion}
								onChange={(e) => setConnectorVersion(e.target.value)}
								size="small"
								fullWidth
							/>
							<MuiTextField
								label="Image processor (optionnel)"
								placeholder="ex: rg.fr-par.scw.cloud/core-services/processor-v2:latest-dev"
								value={processorVersion}
								onChange={(e) => setProcessorVersion(e.target.value)}
								size="small"
								fullWidth
							/>
						</>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Annuler
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color={testMode ? 'warning' : 'primary'}
					disabled={loading || !isValid}
					startIcon={
						loading ? <CircularProgress size={16} /> : <HistoryIcon />
					}
				>
					{testMode ? 'Lancer (Test)' : 'Lancer'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// #region BulkActionButtons
const BulkActionButtons = () => {
	const { selectedIds, getConnectorAccounts } = useConnectorAccounts();
	const notify = useNotify();
	const unselectAll = useUnselectAll('connector-accounts');

	const [forceModalOpen, setForceModalOpen] = useState(false);
	const [historyModalOpen, setHistoryModalOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleForceConfirm = async (options: ForceModalOptions) => {
		const connectorAccounts = getConnectorAccounts();
		if (connectorAccounts.length === 0) {
			notify('Impossible de récupérer les connecteurs', { type: 'error' });
			return;
		}

		setLoading(true);
		try {
			const results = await forceBatch({
				connectorAccounts,
				cleanupOldProcessData: options.cleanupOldProcessData,
				testMode: options.testMode,
				connectorVersion: options.connectorVersion || undefined,
				processorVersion: options.processorVersion || undefined,
			});
			const successCount = results.filter((r) => r.success).length;
			const errorCount = results.filter((r) => !r.success).length;

			const modeLabel = options.testMode ? ' (mode test)' : '';
			if (errorCount === 0) {
				notify(`${successCount} force lancé(s) avec succès${modeLabel}`, {
					type: 'success',
				});
			} else {
				notify(`${successCount} succès, ${errorCount} erreur(s)${modeLabel}`, {
					type: 'warning',
				});
				results
					.filter((r) => !r.success)
					.forEach((r) =>
						console.error(
							`Force failed for ${r.sku}/${r.accountId}: ${r.error}`
						)
					);
			}
			unselectAll();
			setForceModalOpen(false);
		} catch (error: any) {
			notify(error.message || 'Erreur lors du force batch', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const handleHistoryConfirm = async (options: HistoryModalOptions) => {
		const connectorAccounts = getConnectorAccounts();
		if (connectorAccounts.length === 0) {
			notify('Impossible de récupérer les connecteurs', { type: 'error' });
			return;
		}

		setLoading(true);
		try {
			const results = await historyBatch({
				connectorAccounts,
				startDate: options.startDate,
				endDate: options.endDate,
				cleanupOldProcessData: options.cleanupOldProcessData,
				testMode: options.testMode,
				connectorVersion: options.connectorVersion || undefined,
				processorVersion: options.processorVersion || undefined,
			});
			const successCount = results.filter((r) => r.success).length;
			const errorCount = results.filter((r) => !r.success).length;

			const modeLabel = options.testMode ? ' (mode test)' : '';
			if (errorCount === 0) {
				notify(`${successCount} historique(s) lancé(s) avec succès${modeLabel}`, {
					type: 'success',
				});
			} else {
				notify(`${successCount} succès, ${errorCount} erreur(s)${modeLabel}`, {
					type: 'warning',
				});
				results
					.filter((r) => !r.success)
					.forEach((r) =>
						console.error(
							`History failed for ${r.sku}/${r.accountId}: ${r.error}`
						)
					);
			}
			unselectAll();
			setHistoryModalOpen(false);
		} catch (error: any) {
			notify(error.message || "Erreur lors de la reprise d'historique", {
				type: 'error',
			});
		} finally {
			setLoading(false);
		}
	};

	const isDisabled = !selectedIds || selectedIds.length === 0;

	return (
		<>
			<Button
				onClick={() => setForceModalOpen(true)}
				disabled={isDisabled}
				startIcon={<PlayArrowIcon />}
				size="small"
				color="primary"
			>
				Force ({selectedIds?.length || 0})
			</Button>
			<Button
				onClick={() => setHistoryModalOpen(true)}
				disabled={isDisabled}
				startIcon={<HistoryIcon />}
				size="small"
				color="secondary"
				sx={{ ml: 1 }}
			>
				Historique ({selectedIds?.length || 0})
			</Button>

			<ForceModal
				open={forceModalOpen}
				onClose={() => setForceModalOpen(false)}
				onConfirm={handleForceConfirm}
				loading={loading}
				count={selectedIds?.length || 0}
			/>
			<HistoryModal
				open={historyModalOpen}
				onClose={() => setHistoryModalOpen(false)}
				onConfirm={handleHistoryConfirm}
				loading={loading}
				count={selectedIds?.length || 0}
			/>
		</>
	);
};

const ConnectorAccountsFilter = (props: any) => (
	<Filter {...props}>
		<ReferenceInput
			key="sku"
			source="sku"
			reference="connectors"
			alwaysOn
			filter={{
				parentSkuId: ['reverse', 'advertiser', 'sales', 'analytics', 'tag', 'custom'],
			}}
		>
			<AutocompleteInput optionText="name" optionValue="sku" />
		</ReferenceInput>

		<ReferenceInput
			key="project_folder_id"
			source="project_folder_id"
			reference="projects"
			filter={{}}
			alwaysOn
		>
			<AutocompleteInput optionText="name" optionValue="folderId" />
		</ReferenceInput>

		<TextInput label="Account" source="name" alwaysOn />

		<SelectInput
			source="processVersion"
			label="Process Version"
			choices={[
				{ id: '1.0', name: '1.0' },
				{ id: '2.0', name: '2.0' },
			]}
			alwaysOn
		/>

		<TextInput label="Version" source="version" alwaysOn />
	</Filter>
);

// #region ListActions
const ListActions = () => {
	const notify = useNotify();
	const [loadingQdrant, setLoadingQdrant] = useState(false);

	const handleSyncToQdrant = async () => {
		setLoadingQdrant(true);
		try {
			const result = await syncAllSchemas();
			if (result.success) {
				notify(result.message || 'Sync to Qdrant completed', { type: 'success' });
			} else {
				notify(result.message || 'Sync failed', { type: 'error' });
			}
		} catch (error: any) {
			notify(error.message || 'Error syncing to Qdrant', { type: 'error' });
		} finally {
			setLoadingQdrant(false);
		}
	};

	return (
		<TopToolbar>
			<CreateButton />
			<ExportButton />
			<RaButton
				label="Sync Qdrant"
				onClick={handleSyncToQdrant}
				disabled={loadingQdrant}
			>
				{loadingQdrant ? <CircularProgress size={18} /> : <SyncIcon />}
			</RaButton>
		</TopToolbar>
	);
};
// #endregion

export const ConnectorAccountsList = (props: any) => (
	<List {...props} filters={<ConnectorAccountsFilter />} perPage={50} actions={<ListActions />}>
		<Datagrid
			bulkActionButtons={<BulkActionButtons />}
			rowClick={(_id, _basePath, record) =>
				`${record.projectFolderId}/${record.sku}/${record.accountId}`
			}
		>
			<TextField source="project.name" />
			<FunctionField
				source="sku"
				render={(record: any) => {
					if (!record) return null;
					const w = window as any;
					const logoUrl = `${w['FRONT_URL']}/img/connectors-logos/${record.sku}.svg`;
					return (
						<span style={{ display: 'flex', alignItems: 'center' }}>
							<img
								src={logoUrl}
								alt=""
								style={{
									width: 24,
									height: 24,
									marginRight: 8,
									objectFit: 'contain',
								}}
							/>
							{record.sku}
						</span>
					);
				}}
			/>
			<TextField source="accountId" />
			<TextField source="processVersion" />
			<TextField source="version" />
			<TextField source="gitProjectId" />
		</Datagrid>
	</List>
);
